import { useEffect, useState, useRef } from 'react';
import { Box, Flex } from '../../../shared/layouts';
import ProcessHouseSvg from 'imagesUwKluis/svg/processHouse.svg';
import ProcessStep from './ProcessStep';
import styled from '@emotion/styled';

const FadeOut = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 12,
  left: 0,
  zIndex: 3,
  background: `linear-gradient(to right, ${theme.colors.border},  ${theme.colors.border} 50%, rgba(0, 0, 0, 0))`,
}));

const scrollbarStyling = ({ colors }) => ({
  scrollbarColor: `${colors.secondary} rgba(0, 0, 0, 0)`,
  scrollbarWidth: 'thin',
  '::-webkit-scrollbar': {
    height: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: colors.secondary,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0)',
  },
});

const transformSteps = (steps, activeStep) => {
  const processedSteps = steps;
  return {
    steps: processedSteps,
    completed: processedSteps
      .map((_, index) => {
        if (index < processedSteps.indexOf(steps[activeStep])) return index;
        return -1;
      })
      .filter((i) => i >= 0),
    stepName: steps[activeStep],
    currentStep: processedSteps.indexOf(steps[activeStep]),
  };
};

const ProcessSection = ({ data }) => {
  const [activeStep, setActiveStep] = useState();
  const processBar = useRef();
  const scrollbarSize = processBar.current
    ? processBar.current.offsetHeight - processBar.current.clientHeight
    : 0;
  const carouselSteps = transformSteps(data.steps, data.active_step);

  useEffect(() => {
    setActiveStep(carouselSteps.currentStep);
  }, []);

  return (
    <Flex
      alignItems={{ mobile: 'flex-start', desktop: 'center' }}
      flexDirection={{ mobile: 'column', desktop: 'row' }}
      flex={{ mobile: '85% 1 0', desktop: 'auto 0 0' }}
      height={{ mobile: 'unset', desktop: '64px' }}
      width={'100%'}
      pt={{ mobile: 'm', desktop: 0 }}
      bg="border"
      borderRadius={'4px'}
      key={data.uuid}
    >
      <Box
        height={{ mobile: '40px', desktop: '32px' }}
        ml={{ mobile: 'l', desktop: 'm' }}
        mb={{ mobile: 'm', desktop: 0 }}
        flexShrink={0}
      >
        <ProcessHouseSvg
          height={'100%'}
          css={(theme) => ({
            display: 'block',
            fill: theme.colors.secondary,
          })}
        />
      </Box>
      <Box
        flexGrow={1}
        ml={{ mobile: 'l', desktop: 'm' }}
        mb={{ mobile: 's', desktop: 0 }}
        fontWeight={'bold'}
        color="primaryFont"
      >
        <Box fontScale={'s'}>
          {data.name} - {activeStep + 1}/{carouselSteps?.steps.length}
        </Box>
        <Box fontScale={'l'}>{carouselSteps?.steps[activeStep]}</Box>
      </Box>

      <Box
        position="relative"
        width={'100%'}
        maxWidth={{ mobile: '100%', desktop: '45%' }}
        height={{ mobile: '70px', desktop: '100%' }}
      >
        <FadeOut width={{ mobile: '24px', desktop: '16px' }} />
        <Box
          ref={processBar}
          overflowX={'auto'}
          overflowY={'hidden'}
          pt={scrollbarSize}
          height={'100%'}
          css={scrollbarStyling}
        >
          <Flex
            alignItems={'center'}
            justifyContent={'space-between'}
            pl={{ mobile: 's', desktop: 0 }}
            margin={'0'}
            height={'100%'}
            css={{
              '> div': {
                ':first-of-type': {
                  marginLeft: '16px',
                },
                ':last-of-type': {
                  paddingRight: '16px',
                },
                '+ div': {
                  marginLeft: '32px',
                },
              },
            }}
          >
            {carouselSteps?.steps.map((step, index) => {
              return (
                <ProcessStep
                  key={index}
                  index={index}
                  activeStepIndex={activeStep}
                  isCurrent={carouselSteps.currentStep === index}
                  isCompleted={carouselSteps?.completed.indexOf(index) > -1}
                  onClickCallback={setActiveStep}
                  processBar={processBar}
                />
              );
            })}
          </Flex>
        </Box>
        <Box
          bg="secondary"
          flexGrow={1}
          left={{ mobile: '24px', desktop: '16px' }}
          css={{
            position: 'absolute',
            right: 0,
            top: 'calc(50% - 1px)',
            height: '2px',
            zIndex: 1,
          }}
        />
      </Box>
    </Flex>
  );
};

export default ProcessSection;
